import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import { useTemplate } from '../../context/TemplateContext';

const Navbar = ({ htmlContainerRef }) => {
  const { template, generateHTML, saveTemplate, resetTemplate, undo, redo, canUndo, canRedo, parseUploadedHTML } = useTemplate();
  const [isExporting, setIsExporting] = useState(false);
  const [templateName, setTemplateName] = useState(template.name);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showUploadWarning, setShowUploadWarning] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');
  const fileInputRef = useRef(null);

  const handleExport = async () => {
    if (!htmlContainerRef || !htmlContainerRef.current) return;
    const html = htmlContainerRef.current.innerHTML;
    try {
      // Use the modern Clipboard API for rich HTML copy
      await navigator.clipboard.write([
        new window.ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([htmlContainerRef.current.innerText || html], { type: 'text/plain' })
        })
      ]);
      setIsExporting(true);
      setTimeout(() => setIsExporting(false), 2000);
    } catch (err) {
      // fallback: select and copy
      const range = document.createRange();
      range.selectNodeContents(htmlContainerRef.current);
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
      try {
        document.execCommand('copy');
        setIsExporting(true);
        setTimeout(() => setIsExporting(false), 2000);
      } catch (e) {
        alert('Copy failed. Please try manually.');
      }
      selection.removeAllRanges();
    }
  };

  const handleSave = () => {
    if (!htmlContainerRef || !htmlContainerRef.current) return;
    const html = htmlContainerRef.current.innerHTML;
    // Wrap in a minimal HTML document for download
    const fullHtml = `<!DOCTYPE html><html><head><meta charset='utf-8'><title>Email Template</title></head><body>${html}</body></html>`;
    const blob = new Blob([fullHtml], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'email-template.html';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };
  
  const handleUploadClick = () => {
    setShowUploadWarning(true);
  };
  
  const confirmUpload = () => {
    setShowUploadWarning(false);
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  
  const cancelUpload = () => {
    setShowUploadWarning(false);
  };
  
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Only accept HTML files
    if (file.type !== 'text/html' && !file.name.endsWith('.html')) {
      setUploadStatus('Error: Please upload an HTML file');
      setTimeout(() => setUploadStatus(''), 3000);
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const htmlContent = event.target.result;
      const success = parseUploadedHTML(htmlContent);
      
      if (success) {
        setUploadStatus('HTML uploaded successfully!');
      } else {
        setUploadStatus('Error parsing HTML');
      }
      
      setTimeout(() => setUploadStatus(''), 3000);
    };
    
    reader.onerror = () => {
      setUploadStatus('Error reading file');
      setTimeout(() => setUploadStatus(''), 3000);
    };
    
    reader.readAsText(file);
    
    // Reset the file input so the same file can be uploaded again if needed
    e.target.value = '';
  };

  return (
    <NavbarContainer>
      <LogoContainer href="https://htmlstash.com/" target="_blank" rel="noopener noreferrer">
        <LogoText>HTML Stash</LogoText>
      </LogoContainer>
      
      <Title>Email Template Builder</Title>
      
      <ActionButtons>
        <ActionButton onClick={() => undo()} disabled={!canUndo} title="Undo">
          <i className="fas fa-undo"></i> Undo
        </ActionButton>
        <ActionButton onClick={() => redo()} disabled={!canRedo} title="Redo">
          <i className="fas fa-redo"></i> Redo
        </ActionButton>
        <ActionButton onClick={handleSave} title="Download HTML file">
          <i className="fas fa-save"></i> Save as HTML
        </ActionButton>
        <ActionButton onClick={handleUploadClick} title="Upload HTML file" className={uploadStatus.includes('success') ? 'success' : uploadStatus.includes('Error') ? 'danger' : ''}>
          <i className="fas fa-upload"></i> {uploadStatus || 'Upload HTML'}
          <input 
            type="file" 
            ref={fileInputRef} 
            style={{ display: 'none' }} 
            accept=".html,text/html" 
            onChange={handleFileChange} 
          />
        </ActionButton>
        <ActionButton onClick={handleExport} className={isExporting ? 'success' : ''} title="Copy and Paste in Email (Gmail, Outlook, etc)">
          {isExporting ? 'Copied to Clipboard!' : 'Copy and Paste in Email'}
        </ActionButton>
        <ActionButton onClick={() => resetTemplate()} className="danger" title="Reset Template">
          <i className="fas fa-trash"></i> Reset
        </ActionButton>
      </ActionButtons>

      {showSaveDialog && (
        <SaveDialog>
          <SaveDialogContent>
            <h3>Save Template</h3>
            <input
              type="text"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              placeholder="Template Name"
            />
            <SaveDialogButtons>
              <button onClick={() => setShowSaveDialog(false)}>Cancel</button>
              <button onClick={handleSave} className="primary">Save</button>
            </SaveDialogButtons>
          </SaveDialogContent>
        </SaveDialog>
      )}
      
      {showUploadWarning && (
        <SaveDialog>
          <SaveDialogContent>
            <h3>Warning</h3>
            <p>Only HTML files created with this template builder will work properly. Other HTML files may not be parsed correctly.</p>
            <SaveDialogButtons>
              <button onClick={cancelUpload}>Cancel</button>
              <button onClick={confirmUpload} className="primary">Proceed Anyway</button>
            </SaveDialogButtons>
          </SaveDialogContent>
        </SaveDialog>
      )}
    </NavbarContainer>
  );
};

const NavbarContainer = styled.nav`
  display: flex;
  align-items: center;
  background-color: #ffffff;
  height: 60px;
  padding: 0 20px;
  border-bottom: 1px solid #e0e0e0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

const LogoContainer = styled.a`
  display: flex;
  align-items: center;
  text-decoration: none;
`;

const LogoText = styled.span`
  font-size: 18px;
  font-weight: bold;
  color: #5876ff;
  margin-right: 20px;
`;

const Title = styled.h1`
  font-size: 18px;
  font-weight: 500;
  color: #333;
  margin: 0;
  flex: 1;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 10px;
`;

const ActionButton = styled.button`
  background-color: #f5f5f5;
  border: none;
  border-radius: 4px;
  padding: 8px 12px;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  transition: all 0.2s ease;

  &:hover {
    background-color: #e0e0e0;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &.danger {
    color: #ff5252;
    &:hover {
      background-color: #ffebee;
    }
  }

  &.success {
    background-color: #4caf50;
    color: white;
  }
`;

const SaveDialog = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const SaveDialogContent = styled.div`
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  width: 400px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);

  h3 {
    margin-top: 0;
  }

  input {
    width: 100%;
    padding: 10px;
    margin: 10px 0;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
  }
`;

const SaveDialogButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;

  button {
    padding: 8px 16px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    
    &.primary {
      background-color: #5876ff;
      color: white;
    }
  }
`;

export default Navbar;
